/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useEffect, useMemo, useState } from 'react';
import { Button, Input, Tag } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';

import axios from '../apiClient';
import Alert from '../alert';
import Loader from '../loader';

const formatPeriod = (period) => {
  if (period === 'full_time') return 'FT';
  if (period === 'first_time') return '1H';
  if (period === 'second_time') return '2H';
  return period || '-';
};

const formatBetType = (oddType) => {
  if (oddType === 'hdp') return 'HDP';
  if (oddType === 'ou') return 'O/U';
  return oddType || '-';
};

const getSlipIdFromLocation = (location) => {
  const searchSlipId = new URLSearchParams(location.search).get('slipId');
  return searchSlipId || location.state?.slipId || location.state?.rowData?.slip_id || location.state?.rowData?.slipId || '';
};

const normalizePriceInput = (value, fallback) => {
  const text = String(value ?? '').trim();
  if (!text) return '';
  if (text.startsWith('+') || text.startsWith('-')) return text;
  const fallbackSign = String(fallback || '').trim().charAt(0);
  if (fallbackSign === '+' || fallbackSign === '-') return `${fallbackSign}${text}`;
  return text;
};

const formatMoney = (value) => {
  const amount = Number(value ?? 0);
  if (Number.isNaN(amount)) return value || '0';
  return amount.toLocaleString();
};

const formatSelectedTeam = (selectedTeam) => {
  if (selectedTeam === 'home') return 'Home';
  if (selectedTeam === 'away') return 'Away';
  if (selectedTeam === 'over') return 'Over';
  if (selectedTeam === 'under') return 'Under';
  return selectedTeam || '-';
};

const getStatusColor = (status) => {
  if (status === 'pending') return 'gold';
  if (status === 'win') return 'green';
  if (status === 'loss') return 'red';
  return 'blue';
};

export default function SlipDetailsEdit() {
  const location = useLocation();
  const navigate = useNavigate();
  const slipId = getSlipIdFromLocation(location);
  const summaryRow = location.state?.rowData || {};

  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');
  const [rows, setRows] = useState([]);
  const [slipData, setSlipData] = useState(null);
  const [openAlert, setOpenAlert] = useState(false);
  const [errorMsg, setErrorMsg] = useState({});
  const [edits, setEdits] = useState({});

  const summary = useMemo(() => {
    const data = slipData || {};
    return {
      memberName: summaryRow?.member_name || summaryRow?.member?.username || '-',
      betAmount: data?.betAmount ?? summaryRow?.bet_amount ?? summaryRow?.betAmount ?? '-',
      winnedAmount: data?.winnedAmount ?? summaryRow?.winnedAmount ?? '0',
      slipId: data?.id || slipId,
      shortId: data?.shortId || summaryRow?.shortId || summaryRow?.slip_id || summaryRow?.slipId || slipId,
      totalSelections: rows.length,
      betDate: data?.betDate || summaryRow?.betDate || '',
      slipStatus: data?.slipStatus || summaryRow?.status || '-',
      betStatus: data?.betStatus || summaryRow?.betStatus || '-',
      betType: data?.betType || summaryRow?.betType || '-',
      completed: data?.completed ?? summaryRow?.completed ?? '-',
    };
  }, [rows.length, slipData, slipId, summaryRow]);

  const fetchData = async () => {
    if (!slipId) {
      setErrorMsg({ code: 400, msg: 'Slip ID not found' });
      setOpenAlert(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    const res = await axios.get(`/history/getBetDetailBySlipId?slipId=${slipId}`);
    setLoading(false);
    if (Number(res?.code) !== 200) {
      setErrorMsg({ code: res.code, msg: res.message });
      setOpenAlert(true);
      return;
    }

    const detail = res?.data || {};
    setSlipData(detail);
    const betRows = Array.isArray(detail?.bet) ? detail.bet : [];
    const normalizedRows = betRows.map((item, index) => {
      const oddType = item?.betData?.odd_type || '-';
      const currentOdd = item?.odd?.[oddType] || {};
      const betPrice = item?.betData?.raw?.[1] || currentOdd?.raw?.[1] || '';
      const betLine = item?.betData?.raw?.[0] || currentOdd?.raw?.[0] || '';
      return {
        key: item?.id || item?._id || `${slipId}-${index}`,
        betId: item?.id || item?._id || '',
        slipId,
        matchId: item?.match?.matchId || item?.betData?.match_id || '',
        matchName: `${item?.match?.homeTeam?.name_en || '-'} vs ${item?.match?.awayTeam?.name_en || '-'}`,
        leagueName: item?.match?.league?.name_en || item?.match?.league?.name_mm || '-',
        betDate: detail?.betDate || item?.createdAt || '',
        matchDate: item?.match?.matchDate || '',
        periods: item?.betData?.periods || '',
        oddType,
        selectedTeam: item?.betData?.selected_team || '-',
        status: item?.status || '-',
        slipStatus: detail?.slipStatus || summaryRow?.status || '-',
        betConverted: item?.betData?.converted || `${betLine}${betPrice}`,
        marketConverted: currentOdd?.converted || '-',
        line: betLine,
        price: betPrice,
        odd: item?.odd || {},
        betData: item?.betData || {},
        match: item?.match || {},
        rawItem: item,
      };
    });
    setRows(normalizedRows);
    setEdits(
      normalizedRows.reduce((acc, row) => {
        acc[row.key] = {
          line: row.line,
          price: row.price,
        };
        return acc;
      }, {})
    );
  };

  useEffect(() => {
    fetchData();
  }, [slipId]);

  const handleCloseAlert = () => setOpenAlert(false);

  const handleChangeEdit = (key, field, value) => {
    setEdits((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        [field]: value,
      },
    }));
  };

  const getPreparedRowUpdate = (row) => {
    const editValue = edits[row.key] || {};
    const basePrice = row?.price || '';
    const baseLine = String(row?.line || '').trim();
    const nextLine = String(editValue?.line ?? '').trim();
    const nextPrice = normalizePriceInput(editValue?.price, basePrice);
    return {
      basePrice,
      baseLine,
      nextLine,
      nextPrice,
    };
  };

  const changedRows = rows.filter((row) => {
    const { basePrice, baseLine, nextLine, nextPrice } = getPreparedRowUpdate(row);
    return Boolean(nextLine && nextPrice) && (nextLine !== baseLine || nextPrice !== basePrice);
  });

  const buildPayloadForRow = (row) => {
    const { nextLine, nextPrice } = getPreparedRowUpdate(row);
    const currentOddType = row.oddType === 'ou' ? 'ou' : 'hdp';
    const targetOdd = row?.odd?.[currentOddType] || {};
    const updatedBetData = {
      ...(row?.betData || {}),
      raw: [nextLine, nextPrice],
      converted: `${nextLine}${nextPrice}`,
      odd_id: row?.betData?.odd_id || row?.odd?.odd_id || '',
      periods: row?.betData?.periods || row?.periods || '',
      match_id: row?.betData?.match_id || row?.matchId || '',
      odd_type: row?.betData?.odd_type || currentOddType,
      selected_team: row?.betData?.selected_team || row?.selectedTeam || '',
    };
    const nextOddObject = {
      ...row.odd,
      [currentOddType]: {
        ...targetOdd,
        raw: [nextLine, nextPrice],
        converted: `${nextLine}${nextPrice}`,
      },
    };

    return {
      matchId: row.matchId,
      periods: row.periods,
      oddId: row?.odd?.odd_id || row?.betData?.odd_id || '',
      hdp: nextOddObject?.hdp,
      ou: nextOddObject?.ou,
      slipId: row.slipId,
      betData: updatedBetData,
    };
  };

  const handleSaveAll = async () => {
    if (changedRows.length === 0) {
      setErrorMsg({ info: true, msg: 'save လုပ်ဖို့ပြောင်းထားတဲ့ odd မရှိသေးပါ' });
      setOpenAlert(true);
      return;
    }

    for (const row of changedRows) {
      const { nextLine, nextPrice } = getPreparedRowUpdate(row);
      if (!nextLine) {
        setErrorMsg({ info: true, msg: 'Line value ထည့်ပေးပါ' });
        setOpenAlert(true);
        return;
      }
      if (!nextPrice) {
        setErrorMsg({ info: true, msg: 'Odd value ထည့်ပေးပါ' });
        setOpenAlert(true);
        return;
      }
    }

    setSavingKey('all');
    for (const row of changedRows) {
      const payload = buildPayloadForRow(row);
      const res = await axios.put(`/bet/editOddBySlipId`, payload);
      if (Number(res?.code) !== 200) {
        setSavingKey('');
        setErrorMsg({ code: res.code, msg: res.message });
        setOpenAlert(true);
        return;
      }
    }
    setSavingKey('');
    setErrorMsg({ code: 200, msg: 'odds updated successfully' });
    setOpenAlert(true);
    fetchData();
  };

  return (
    <div>
      <Alert openAlert={openAlert} errorMsg={errorMsg} handleCloseAlert={handleCloseAlert} />
      {loading && <Loader />}
      {!loading && (
        <div style={{ padding: '0 4rem 2rem' }}>
          <div
            css={css`
              margin: 1rem 0 1.5rem 0;
              border-radius: 1rem;
              box-shadow: -2px 2px 12px -3px rgba(0, 0, 0, 0.3);
            `}
            style={{
              padding: '1.25rem 1.5rem',
              backgroundColor: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              gap: '1rem',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
              <h4 style={{ margin: 0, color: 'black', fontWeight: 'normal' }}>Slip ID: {summary.shortId}</h4>
              <h4 style={{ margin: 0, color: 'black', fontWeight: 'normal' }}>Member: {summary.memberName}</h4>
              <h4 style={{ margin: 0, color: 'black', fontWeight: 'normal' }}>Type: {summary.betType}</h4>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
              <h4 style={{ margin: 0, color: 'black', fontWeight: 'normal' }}>Bet Amount: {formatMoney(summary.betAmount)}</h4>
              <h4 style={{ margin: 0, color: 'black', fontWeight: 'normal' }}>Win Amount: {formatMoney(summary.winnedAmount)}</h4>
              <h4 style={{ margin: 0, color: 'black', fontWeight: 'normal' }}>Selections: {summary.totalSelections}</h4>
              <h4 style={{ margin: 0, color: 'black', fontWeight: 'normal' }}>
                Bet Date: {summary.betDate ? moment(summary.betDate).format('DD-MM-YYYY h:mm A') : '-'}
              </h4>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Tag color="purple" style={{ padding: '0.35rem 0.65rem', margin: 0 }}>
                Slip: {summary.slipStatus}
              </Tag>
              <Tag color={String(summary.completed) === 'true' ? 'green' : 'default'} style={{ padding: '0.35rem 0.65rem', margin: 0 }}>
                Completed: {String(summary.completed)}
              </Tag>
              <Button type="primary" loading={savingKey === 'all'} disabled={changedRows.length === 0} onClick={handleSaveAll}>
                Save
              </Button>
              <Button onClick={() => navigate(-1)}>Back</Button>
            </div>
          </div>

          <div
            css={css`
              border-radius: 1rem;
              box-shadow: -2px 2px 12px -3px rgba(0, 0, 0, 0.2);
            `}
            style={{ backgroundColor: 'white', padding: '1rem 1.1rem' }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {rows.map((row) => {
                const editValue = edits[row.key] || {};
                const previewLine = String(editValue?.line ?? row.line ?? '').trim();
                const previewPrice = normalizePriceInput(editValue?.price, row.price);
                const previewConverted = previewLine && previewPrice ? `${previewLine}${previewPrice}` : '-';
                return (
                  <div
                    key={row.key}
                    css={css`
                      border: 1px solid #ececec;
                      border-radius: 0.85rem;
                      box-shadow: 0 6px 18px rgba(15, 23, 42, 0.05);
                    `}
                    style={{
                      padding: '0.85rem 0.95rem',
                      background: 'linear-gradient(180deg, #ffffff 0%, #fbfbff 100%)',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.7rem' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                        <h4 style={{ margin: 0, color: 'black', fontSize: '1rem' }}>{row.matchName}</h4>
                        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
                          <span style={{ color: '#5b5b5b', fontSize: '0.87rem' }}>{row.leagueName}</span>
                          <span style={{ color: '#b0b0b0' }}>•</span>
                          <span style={{ color: '#5b5b5b', fontSize: '0.87rem' }}>
                            {row.matchDate ? moment(row.matchDate).format('DD-MM-YYYY h:mm A') : '-'}
                          </span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <Tag color={row.oddType === 'ou' ? 'orange' : 'blue'}>{formatBetType(row.oddType)}</Tag>
                        <Tag color="purple">{formatPeriod(row.periods)}</Tag>
                        <Tag color={getStatusColor(row.status)}>{row.status || '-'}</Tag>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', alignItems: 'end' }}>
                      <div
                        css={css`
                          flex: 1 1 320px;
                        `}
                        style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', alignItems: 'end' }}
                      >
                        <CompactInfo label="Selected" value={formatSelectedTeam(row.selectedTeam)} />
                        <CompactInfo label="Bet Odd" value={row.betConverted} />
                        <CompactInfo label="Market Odd" value={row.marketConverted} />
                      </div>

                      <div
                        css={css`
                          flex: 1 1 420px;
                          border-radius: 0.8rem;
                          background: #fcfcff;
                          border: 1px solid #ecebff;
                        `}
                        style={{ padding: '0.75rem 0.85rem' }}
                      >
                        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'end' }}>
                          <div style={{ flex: '1 1 100px' }}>
                            <h5 style={{ margin: '0 0 0.3rem 0', color: '#6b7280', fontWeight: 'normal' }}>New Line</h5>
                            <Input
                              size="small"
                              value={editValue?.line}
                              onChange={({ target }) => handleChangeEdit(row.key, 'line', target.value)}
                              placeholder="0"
                            />
                          </div>
                          <div style={{ flex: '1 1 120px' }}>
                            <h5 style={{ margin: '0 0 0.3rem 0', color: '#6b7280', fontWeight: 'normal' }}>New Odd</h5>
                            <Input
                              size="small"
                              value={editValue?.price}
                              onChange={({ target }) => handleChangeEdit(row.key, 'price', target.value)}
                              placeholder="+95"
                            />
                          </div>
                          <div style={{ flex: '1 1 120px' }}>
                            <h5 style={{ margin: '0 0 0.3rem 0', color: '#6b7280', fontWeight: 'normal' }}>Preview</h5>
                            <div
                              css={css`
                                min-height: 24px;
                                border-radius: 0.5rem;
                                border: 1px dashed #b8b4ff;
                              `}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '0 0.6rem',
                                backgroundColor: '#f7f6ff',
                                color: '#4338ca',
                                fontWeight: 600,
                                fontSize: '0.9rem',
                              }}
                            >
                              {previewConverted}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {rows.length === 0 && (
                <div style={{ padding: '2rem 1rem', textAlign: 'center', color: '#6b7280' }}>
                  bet data မရှိသေးပါ
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CompactInfo({ label, value }) {
  return (
    <div style={{ minWidth: '88px', display: 'flex', flexDirection: 'column', gap: '0.18rem' }}>
      <h5 style={{ margin: 0, color: '#6b7280', fontWeight: 'normal', fontSize: '0.78rem' }}>{label}</h5>
      <h4 style={{ margin: 0, color: '#111827', fontSize: '0.95rem' }}>{value || '-'}</h4>
    </div>
  );
}
